import React, { ReactNode, useContext } from "react";
import { flatMap, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { buildLink } from "common/routing";
import { FIRMWithWarnings } from "common-client/utils/firmInfoWarnings";
import {
  getDFEElevationConfiguration,
  getDFETooltipInterpolations,
  getDFETooltipKey,
  isUserOverrideable,
  Property,
} from "common-client/utils/firms";
import {
  DesignFloodElevationType,
  DfeRuleType,
} from "../../../../../../generated/graphql";
import { track } from "../../../../../../utils/tracking";
import { AuthContext } from "../../../../../Authorization/AuthContext";
import { useDesignFloodElevationEditModal } from "../../DesignFloodElevationEditModal";
import { FIRMInfoContext } from "../../FIRMInfoContext";
import { RenderMode } from "../../types";
import { FIRMTableTooltip } from "..";
import { RequestInfoLink } from "../__styles__/FIRMTable";
import { FIRMInfoRow } from "./FIRMInfoRow";
interface DesignFloodElevationRowProps {
  property?: Maybe<Property>;
  size: RenderMode;
  hasRequestBFEHelpOption?: boolean;
  isPublic: boolean;
  label: string;
}

export const DesignFloodElevationRow = ({
  property,
  size,
  hasRequestBFEHelpOption = false,
  label,
  isPublic: dfeAttributeIsPublic,
}: DesignFloodElevationRowProps) => {
  const { authorized, account, isPublic } = useContext(AuthContext);
  const { activeFirmIds, firms } = useContext(FIRMInfoContext);
  const { t } = useTranslation();
  const activeFirms = firms.filter(firm => activeFirmIds.includes(firm.id));

  const designFloodElevation = property?.designFloodElevation;
  const value = designFloodElevation?.value;
  const dfeRuleType = designFloodElevation?.calculated?.rule?.type;
  const override = designFloodElevation?.override;
  const isFromOverride = !!designFloodElevation?.override;

  const type =
    override ?
      DesignFloodElevationType.CUSTOM
    : DesignFloodElevationType.DEFAULT;
  const [showDesignFloodElevationModal] = useDesignFloodElevationEditModal({
    dfe: { type, stringValue: value },
    property,
  });

  if (
    !isUserOverrideable(property?.designFloodElevation ?? null) ||
    activeFirms.every(firm => !firm.showDFE)
  ) {
    return null;
  }

  const valueForFIRM = (firm: FIRMWithWarnings) => {
    const { value, canUpdateDFE, canRequestDFE } = getDFEElevationConfiguration(
      {
        firm,
        property,
        authorized,
        isPublic,
        hasRequestBFEHelpOption,
      },
    );
    return (
      value ??
      (canUpdateDFE ? "Add" : null) ??
      (canRequestDFE ? "Request info" : null)
    );
  };

  let tooltip: Maybe<ReactNode> = null;

  if (
    (dfeRuleType !== DfeRuleType.AUTOMATIC &&
      dfeRuleType !== DfeRuleType.MINIMUM) ||
    isNil(value) ||
    isFromOverride
  ) {
    tooltip = null;
  } else {
    let tooltipMarkdown = null;
    const rule = designFloodElevation?.calculated?.rule;

    if (rule) {
      const tooltipTranslationKey = getDFETooltipKey(
        rule,
        flatMap(
          account?.propertyInformationSections.map(
            section => section.accountPropertyAttributes,
          ),
        ),
      );

      const tooltipTranslationInterpolations = getDFETooltipInterpolations(
        rule,
        flatMap(
          account?.propertyInformationSections.map(
            section => section.accountPropertyAttributes,
          ),
        ),
      );

      tooltipMarkdown =
        tooltipTranslationKey ?
          t(tooltipTranslationKey, tooltipTranslationInterpolations)
        : "";
    }

    tooltip = (
      <FIRMTableTooltip
        isPublicOnly={false}
        tooltipMarkdown={tooltipMarkdown}
      />
    );
  }

  const wrapper = ({
    children,
    firmId,
  }: {
    children?: ReactNode;
    firmId: string;
  }) => {
    const firm = activeFirms.find(firm => firm.id === firmId)!;
    const { canUpdateDFE, canRequestDFE } = getDFEElevationConfiguration({
      firm,
      property,
      authorized,
      isPublic,
      hasRequestBFEHelpOption,
    });

    if (canRequestDFE) {
      return (
        <RequestInfoLink
          to={buildLink(
            "getHelp",
            {},
            { propertyId: property?.id, reasonCodes: "requestBFE" },
          )}
          onClick={() => track("Request DFE")}
        >
          {children}
        </RequestInfoLink>
      );
    }

    if (canUpdateDFE) {
      return (
        <a
          onClick={() => showDesignFloodElevationModal()}
          data-testid={"editDfe"}
        >
          {children}
        </a>
      );
    } else {
      return <>{children}</>;
    }
  };

  return (
    <FIRMInfoRow
      property={"dfe"}
      valueForFIRM={valueForFIRM}
      label={label}
      wrap={wrapper}
      size={size}
      tooltip={tooltip}
      isPublic={dfeAttributeIsPublic}
    />
  );
};
